import { useEffect, useState } from "react";
import moment from "moment";
import _ from "lodash";
import { toast } from "react-toastify";

import { getAvatarUrl, getImageUrl } from "src/Common/common";
import TechnicianService from "src/Services/API/technician.service";
import { handleError, isAccountant, isAdmin, isManagerOver } from "src/Common/function";

const DayTechItem = ({
  tech,
  currentDate,
  startDateString,
  timesheetUpdated,
  activeEditingTechId,
  setActiveEditingTechId,
  woStatus,
}) => {

  const [techData, setTechData] = useState(null);

  const [editedTimes, setEditedTimes] = useState({
    regularTime: "00:00",
    travelTime: "00:00",
    overTime: "00:00",
  });
  const isEditing = activeEditingTechId === tech?.id;

  const normalizeTimeValue = (value) => {
    if (!value || typeof value !== "string") return "00:00";
    const trimmed = value.trim();
    if (!trimmed || trimmed === "__:__") return "00:00";

    const parts = trimmed.split(":");
    if (parts.length !== 2) return "00:00";

    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return "00:00";
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return "00:00";

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const formatTimeInput = (rawValue) => {
    const digits = String(rawValue || "").replace(/\D/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;

    const isThreeDigitTime = digits.length === 3;
    const hoursPart = isThreeDigitTime ? digits.slice(0, 1) : digits.slice(0, 2);
    const minutesPart = isThreeDigitTime ? digits.slice(1) : digits.slice(2);

    const clampedHours = String(Math.min(Number(hoursPart), 23)).padStart(hoursPart.length, "0");
    const clampedMinutes = String(Math.min(Number(minutesPart), 59)).padStart(minutesPart.length, "0");

    return `${clampedHours}:${clampedMinutes}`;
  };

  const isNormalizedTimeValue = (value) => {
    if (typeof value !== "string") return false;
    if (!/^\d{2}:\d{2}$/.test(value)) return false;
    return normalizeTimeValue(value) === value;
  };

  useEffect(() => {
    let technician = _.cloneDeep(tech);
    if (tech?.timesheet) {
      for (let i = 0; i < tech.timesheet.length; i++) {
        const workDate = moment(startDateString).add(Number(tech.timesheet[i].dayDiff || 0), "days");
        const isSameDate = workDate.isSame(currentDate, "day");

        if (tech.id === 11311) {
          console.log("🚀 ~ useEffect ~ tech:", tech.timesheet[i], workDate, currentDate, isSameDate)
        }

        if (isSameDate) {
          technician.regularTime = tech.timesheet[i].regularTime;
          technician.travelTime = tech.timesheet[i].travelTime;
          technician.overTime = tech.timesheet[i].overTime;
        }
      }
    }

    setTechData(technician);
    setEditedTimes({
      regularTime: normalizeTimeValue(technician.regularTime),
      travelTime: normalizeTimeValue(technician.travelTime),
      overTime: normalizeTimeValue(technician.overTime),
    });
  }, [tech, currentDate, startDateString]);


  const renderTimeField = (fieldName) => {
    if (!isEditing) {
      const value = techData?.[fieldName];
      return (
        <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor w-[96px] text-center">
          {value && value !== "00:00" ? value : "__:__"}
        </p>
      );
    }

    return (
      <input
        value={editedTimes[fieldName]}
        onChange={(e) => {
          const nextValue = formatTimeInput(e.target.value);
          setEditedTimes((previous) => ({ ...previous, [fieldName]: nextValue }));
        }}
        className="w-[97px] h-[34px] text-center rounded-[10px] border-[1px] border-borderColor bg-white font-OpenSansRegular text-[14px] text-titleColor outline-none"
      />
    );
  };


  const onEditButtonClick = () => {
    if (!isEditing) {
      if (activeEditingTechId && activeEditingTechId !== tech.id) {
        return;
      }
      setActiveEditingTechId(tech.id);
      return;
    }

    updateTimesheet();
    setActiveEditingTechId(null);
  };

  const updateTimesheet = () => {
    const originalTimes = {
      regularTime: normalizeTimeValue(techData?.regularTime),
      travelTime: normalizeTimeValue(techData?.travelTime),
      overTime: normalizeTimeValue(techData?.overTime),
    };

    let changedTimes = {};
    if (
      editedTimes.regularTime !== originalTimes.regularTime &&
      isNormalizedTimeValue(editedTimes.regularTime)
    ) {
      changedTimes.regularTime = editedTimes.regularTime;
    }
    if (
      editedTimes.travelTime !== originalTimes.travelTime &&
      isNormalizedTimeValue(editedTimes.travelTime)
    ) {
      changedTimes.travelTime = editedTimes.travelTime;
    }
    if (
      editedTimes.overTime !== originalTimes.overTime &&
      isNormalizedTimeValue(editedTimes.overTime)
    ) {
      changedTimes.overTime = editedTimes.overTime;
    }

    if (Object.keys(changedTimes).length === 0) {
      return;
    }


    const dayDiff = moment(currentDate).diff(moment(startDateString), "days");

    let timesheet = [];
    if (techData.timesheet && techData.timesheet.length > 0) {
      timesheet = _.cloneDeep(techData.timesheet);
      if (timesheet) {
        let isFound = false;
        for (let i = 0; i < timesheet.length; i++) {
          if (timesheet[i].dayDiff === dayDiff) {
            if (changedTimes.regularTime) {
              timesheet[i].regularTime = changedTimes.regularTime;
            }
            if (changedTimes.travelTime) {
              timesheet[i].travelTime = changedTimes.travelTime;
            }
            if (changedTimes.overTime) {
              timesheet[i].overTime = changedTimes.overTime;
            }
            isFound = true;
          }
        }
        if (!isFound) {
          let temp = _.cloneDeep(changedTimes);
          temp.dayDiff = dayDiff;
          timesheet.push(temp);
        }
      }
    }
    else {
      let temp = _.cloneDeep(changedTimes);
      temp.dayDiff = dayDiff;
      timesheet.push(temp);
    }

    TechnicianService.update({
      id: techData.id,
      data: { timesheet: JSON.stringify(timesheet) }
    })
      .then((response) => {
        console.log("🚀 ~ updateTimesheet ~ response:", response)
        toast("Timesheet updated successfully");
        timesheetUpdated();
      })
      .catch((error) => {
        handleError(error);
      });
  }

  return (
    techData &&
    <div className="w-full h-[40px] flex items-center px-3 border-t-[1px] border-borderColor relative">
      <img src={getAvatarUrl(techData.user.avatar)} className="w-[24px] rounded-[12px]" />
      <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor ml-2">{techData.user.name}</p>

      <div className="absolute right-[5%] top-[3px] h-[34px] flex items-center gap-3">
        {renderTimeField("regularTime")}
        {renderTimeField("travelTime")}
        {renderTimeField("overTime")}
      </div>

      {(isManagerOver() || isAccountant()) && woStatus < 100 && (
        <div className="absolute right-[1%] top-[3px]">
          <EditButton
            clicked={onEditButtonClick}
            isEditing={isEditing}
            isDisabled={!isEditing && activeEditingTechId !== null && activeEditingTechId !== tech.id}
          />
        </div>
      )}

      {isAdmin() && woStatus === 100 && (
        <div className="absolute right-[1%] top-[3px]">
          <EditButton
            clicked={onEditButtonClick}
            isEditing={isEditing}
            isDisabled={!isEditing && activeEditingTechId !== null && activeEditingTechId !== tech.id}
          />
        </div>
      )}
    </div>
  );
};

export default DayTechItem;

const EditButton = ({ clicked, isEditing = false, isDisabled = false }) => {
  return (
    isEditing ? (
      <div onClick={clicked} className="w-[18px] absolute right-[5%] top-[12px] cursor-pointer">
        <img src={getImageUrl("/images/common/check.png")} className="w-[18px]" />
      </div>
    ) : (
      <div
        onClick={isDisabled ? undefined : clicked}
        className={`w-[18px] absolute right-[5%] top-[12px] ${isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
      >
        <img src={getImageUrl("/images/common/blueedit.png")} className="w-[18px]" />
      </div>
    )
  );
};